import React, { useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { intl, DmsPage, DmsButton, DmsAltBox } from "dmsbasereact";
import { Box, Grid, Chip, Stack, Tooltip, IconButton, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import TuneIcon from "@mui/icons-material/Tune";
import HistoryIcon from "@mui/icons-material/History";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { useDispensationInsights } from "./useDispensationInsights";
import { SummaryCards } from "./components/SummaryCards";
import { InsightsChart } from "./components/InsightsChart";
import { TopLists } from "./components/TopLists";
import { InsightsTable } from "./components/InsightsTable";
import { FilterPopover } from "./components/FilterPopover";

const formatPercentage = (value) => `${(value * 100).toFixed(1)}%`;

const buildPreFilters = (state) => {
    const nextFilters = [];
    if (state.startDate) {
        nextFilters.push({ name: "start_date", value: state.startDate });
    }
    if (state.endDate) {
        nextFilters.push({ name: "end_date", value: state.endDate });
    }
    if (state.branch) {
        nextFilters.push({ name: "organization_branch_pk", value: state.branch });
    }
    if (state.pharmacist) {
        nextFilters.push({ name: "pharmacist_pk", value: state.pharmacist });
    }
    if (state.batch) {
        nextFilters.push({ name: "batch_pk", value: state.batch });
    }
    return nextFilters;
};

const upsertFilter = (filters, name, value) => {
    const index = filters.findIndex((item) => item.name === name);
    if (index >= 0) {
        filters[index] = { name, value };
    } else {
        filters.push({ name, value });
    }
};

export const DispensationInsightsPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const { state, handlers } = useDispensationInsights();
    const {
        filters,
        preFilters,
        options,
        loadingOptions,
        summary,
        chartData
    } = state;

    const {
        setFilters,
        setPreFilters,
        handleApplyFilters,
        handleClearFilters,
        handleRefreshAll,
        handleDownload
    } = handlers;

    const currentLocale = intl.getInitLocale?.() || intl.options?.currentLocale || "default";

    const formatNumberValue = useCallback(
        (value) => new Intl.NumberFormat(currentLocale).format(value ?? 0),
        [currentLocale]
    );

    const handleRemoveChip = useCallback(
        (key) => {
            setFilters((prev) => {
                const next = { ...prev };
                switch (key) {
                    case "date":
                        next.startDate = null;
                        next.endDate = null;
                        break;
                    case "branch":
                        next.branch = "";
                        break;
                    case "pharmacist":
                        next.pharmacist = "";
                        break;
                    case "batch":
                        next.batch = "";
                        break;
                    default:
                        break;
                }
                setPreFilters(buildPreFilters(next));
                return next;
            });
        },
        [setFilters, setPreFilters]
    );

    const handleViewHistory = useCallback(
        (row) => {
            const historyFilters = [...preFilters];
            if (row.organization_branch_pk) {
                upsertFilter(historyFilters, "organization_branch_pk", String(row.organization_branch_pk));
            }
            if (row.pharmacist_pk) {
                upsertFilter(historyFilters, "pharmacist_pk", String(row.pharmacist_pk));
            }
            if (filters.startDate) {
                upsertFilter(historyFilters, "start_date", filters.startDate);
            }
            if (filters.endDate) {
                upsertFilter(historyFilters, "end_date", filters.endDate);
            }

            navigate(APP_ROUTES.DispensationHistory, {
                state: { preFilters: historyFilters },
            });
        },
        [filters.endDate, filters.startDate, navigate, preFilters]
    );

    const tableColumns = useMemo(
        () => [
            {
                label: intl.get("Institution").d("Institution"),
                column_name: "organization_branch_name",
                width: "220px",
            },
            {
                label: intl.get("Pharmacist").d("Pharmacist"),
                column_name: "pharmacist_name",
                width: "200px",
            },
            {
                label: intl.get("Batch").d("Batch"),
                column_name: "batch_name",
                width: "220px",
            },
            {
                label: intl.get("DispensationInsights.Table.TotalPrescribed").d("Prescribed"),
                column_name: "total_prescribed",
                width: "140px",
                function: (value) => formatNumberValue(value),
            },
            {
                label: intl.get("DispensationInsights.Table.TotalDispensed").d("Dispensed"),
                column_name: "total_dispensed",
                width: "140px",
                function: (value) => formatNumberValue(value),
            },
            {
                label: intl.get("DispensationInsights.Table.DivergenceQuantity").d("Divergence"),
                column_name: "divergence_quantity",
                width: "150px",
                function: (value, row) => (
                    <Typography color={row.above_threshold ? "error.main" : "text.primary"} variant="body2">
                        {formatNumberValue(value)}
                    </Typography>
                ),
            },
            {
                label: intl.get("DispensationInsights.Table.DivergenceRate").d("Divergence rate"),
                column_name: "divergence_rate",
                width: "150px",
                function: (value, row) => (
                    <Typography color={row.above_threshold ? "error.main" : "text.secondary"} variant="body2">
                        {formatPercentage(value)}
                    </Typography>
                ),
            },
            {
                label: intl.get("Actions").d("Actions"),
                column_name: "actions",
                width: "90px",
                element: (row) => (
                    <Tooltip title={intl.get("DispensationInsights.Table.ViewHistory").d("Open history")}>
                        <span>
                            <IconButton onClick={() => handleViewHistory(row)} size="small">
                                <HistoryIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
            },
        ],
        [formatNumberValue, handleViewHistory]
    );

    const summaryCards = useMemo(() => {
        const data = summary ?? {
            total_prescribed: 0,
            total_dispensed: 0,
            completion_rate: 0,
            total_dispensations: 0,
            divergent_dispensations: 0,
            divergence_rate: 0,
            top_branches: [],
            top_pharmacists: [],
        };
        return [
            {
                key: "total_prescribed",
                label: intl.get("TotalPrescriptions").d("Total Prescriptions"),
                value: formatNumberValue(data.total_prescribed),
                testId: "insights-card-total-prescribed",
            },
            {
                key: "active_prescribed",
                label: intl.get("ActivePrescriptions").d("Active Prescriptions"),
                value: formatNumberValue(data.active_prescribed),
                testId: "insights-card-active-prescribed",
            },
            {
                key: "total_dispensed_boxes",
                label: intl.get("DispensationInsights.Card.DispensedBoxes").d("Dispensed Boxes"),
                value: formatNumberValue(data.total_dispensed_boxes),
                testId: "insights-total_dispensed_boxes",
            },
            {
                key: "total_dispensed_strength",
                label: intl.get("DispensationInsights.Card.DispensedStrength").d("Dispensed Strength"),
                value: data.total_dispensed_strength,
                testId: "insights-total_dispensed_strength",
            },
        ];
    }, [formatNumberValue, summary]);

    const appliedFilterChips = useMemo(() => {
        const chips = [];
        const branchFilter = preFilters.find((item) => item.name === "organization_branch_pk");
        if (branchFilter) {
            const option = options.organization_branches.find(
                (item) => String(item.value) === String(branchFilter.value)
            );
            chips.push({
                key: "branch",
                label: `${intl.get("Institution").d("Institution")}: ${option?.label || branchFilter.value}`,
            });
        }

        const pharmacistFilter = preFilters.find((item) => item.name === "pharmacist_pk");
        if (pharmacistFilter) {
            const option = options.pharmacists.find(
                (item) => String(item.value) === String(pharmacistFilter.value)
            );
            chips.push({
                key: "pharmacist",
                label: `${intl.get("Pharmacist").d("Pharmacist")}: ${option?.label || pharmacistFilter.value}`,
            });
        }

        const batchFilter = preFilters.find((item) => item.name === "batch_pk");
        if (batchFilter) {
            const option = options.batchs.find(
                (item) => String(item.value) === String(batchFilter.value)
            );
            chips.push({
                key: "batch",
                label: `${intl.get("Batch").d("Batch")}: ${option?.label || batchFilter.value}`,
            });
        }

        const dateStart = preFilters.find((item) => item.name === "start_date");
        const dateEnd = preFilters.find((item) => item.name === "end_date");
        if (dateStart || dateEnd) {
            chips.push({
                key: "date",
                label: `${intl.get("DispensationInsights.Filter.Period").d("Period")}: ${dateStart?.value ? new Date(dateStart.value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"} - ${dateEnd?.value ? new Date(dateEnd.value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}`,
            });
        }

        return chips;
    }, [options, preFilters]);

    return (
        <DmsPage name={intl.get("DispensationInsights.PageTitle").d("Dispensation Insights")}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SummaryCards summaryCards={summaryCards} />
                </Grid>

                <Grid item xs={12} md={8}>
                    <InsightsChart
                        chartDatasets={chartData.datasets}
                        chartLabels={chartData.labels}
                        chartOptions={chartData.options}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TopLists
                        topBranches={summary.top_branches}
                        topPharmacists={summary.top_pharmacists}
                        intl={intl}
                        formatNumberValue={formatNumberValue}
                    />
                </Grid>

                <Grid item xs={12}>
                    <DmsAltBox.Card>
                        <DmsAltBox.Header>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    flexWrap: 'wrap',
                                    gap: 1
                                }}
                            >
                                <Typography variant="h6">
                                    {intl.get("DispensationInsights.Table.Title").d("Dispensation Details")}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <DmsButton
                                        variant="ghost"
                                        onClick={handleRefreshAll}
                                        startIcon={<RefreshIcon fontSize="small" />}
                                    >
                                        {intl.get("Common.Refresh").d("Refresh")}
                                    </DmsButton>
                                    <DmsButton
                                        variant="ghost"
                                        onClick={handleDownload}
                                        startIcon={<DownloadIcon fontSize="small" />}
                                    >
                                        {intl.get("DispensationInsights.Export").d("Export CSV")}
                                    </DmsButton>
                                    <DmsButton
                                        variant="primary"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TuneIcon />}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 2,
                                            border: `1px solid ${theme.palette.primary.main}`
                                        }}
                                    >
                                        {intl.get("DispensationInsights.Filters.Button").d("Filters")}
                                    </DmsButton>
                                </Box>
                            </Box>
                        </DmsAltBox.Header>
                        <DmsAltBox.Body>
                            {appliedFilterChips.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {appliedFilterChips.map((chip) => (
                                            <Chip
                                                key={chip.key}
                                                label={chip.label}
                                                onDelete={() => handleRemoveChip(chip.key)}
                                                sx={{ mb: 0.5 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            <InsightsTable
                                tableRef={tableRef}
                                tableColumns={tableColumns}
                                preFilters={preFilters}
                            />
                        </DmsAltBox.Body>
                    </DmsAltBox.Card>
                </Grid>
            </Grid>

            <FilterPopover
                filterAnchorEl={filterAnchorEl}
                handleCloseFilterPopover={() => setFilterAnchorEl(null)}
                filters={filters}
                setFilters={setFilters}
                loadingOptions={loadingOptions}
                granularityOptions={chartData.granularityOptions}
                handleApplyFilters={() => {
                    handleApplyFilters();
                    setFilterAnchorEl(null);
                }}
                handleClearFilters={() => {
                    handleClearFilters();
                    setFilterAnchorEl(null);
                }}
                options={options}
            />
        </DmsPage>
    );
};

export default DispensationInsightsPage;
